import * as uuid from "uuid";
import React from "react";
import { formatMessage, formatMessageWithParams } from "../../utils/helpers/LanguageHelper";
import { DocumentPart } from "../../api/models";
import { CustomModal } from "../layout/ActionModal";
import LoadingManager from "../../common/loading/LoadingManager";
import { fetchDocumentMetadata, fetchParentsDocumentPart } from "../../api/document/DocumentsPart";
import { TgkDocumentElaborationRequest } from "../../api/models/tgk-document-elaboration-request";
import { store } from "../../redux/store/store";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { alignDocumentMetadataAsync } from "../../redux/feature/document/document-slice";
import { useAppSelector } from "../../redux/hooks/hooks";
import { selectCpmUsername } from "../../redux/feature/settings/configurations-slice";
import { DocumentData } from "./EditDocument";
import NotificationManager from "../../common/notification/NotificationManager";

const loadingManagerKey = "ALIGN_DOCUMENT_PANEL";

interface AlignProps {
  documentPartToAlign?: DocumentPart;
  documentPartToSave?: DocumentData;
  channelIdToNotify: string;
  onCloseModal: () => void;
  saveDocument?: (input?: DocumentData) => void;
}

export const AlignDocumentPanel: React.FunctionComponent<AlignProps> = (props) => {
  const username = useAppSelector(selectCpmUsername);
  const { documentPartToSave, onCloseModal, saveDocument } = props;

  let documentPartToAlign: DocumentPart;
  if (props.documentPartToAlign) {
    documentPartToAlign = props.documentPartToAlign.id
      ? props.documentPartToAlign
      : (props.documentPartToAlign as any).documentPart;
  }

  const alignDocument = async () => {
    LoadingManager.create(loadingManagerKey);
    const parents = await fetchParentsDocumentPart(documentPartToAlign.id);
    const metadata = await fetchDocumentMetadata(documentPartToAlign.id, false);

    if (!metadata) {
      NotificationManager.addNotification({
        title: "",
        message: formatMessage("cof.editdocument.message.metadatanotfound"),
        type: "warning",
        isTemporary: false,
      });

      LoadingManager.remove(loadingManagerKey);
      return;
    }

    const documentPart: DocumentPart = {
      name: documentPartToAlign.name.replace(/\.docx$/, "") + ".docx",
      description: documentPartToAlign.description,
      id: documentPartToAlign.id,
      siteId: documentPartToAlign.siteId,
      driveItemId: documentPartToAlign.driveItemId,
      metadata: metadata,
      multiLinked:
        parents.length > 1 || documentPartToAlign.multiLinked || (parents.length == 1 && parents[0].multiLinked),
      hasChildren: documentPartToAlign.hasChildren,
      unaligned: documentPartToAlign.unaligned,
      unalignedSubParts: documentPartToAlign.unalignedSubParts,
    };

    const tgkDocumentElaborationRequest: TgkDocumentElaborationRequest = {
      correlationId: uuid.v4(),
      clientId: store.getState().configurationState.clientId,
      userId: username,
      channelIdToNotify: props.channelIdToNotify ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      documentId: documentPart.id,
      documentPart: documentPart,
      isOpenDocument: false,
      isAlignNeeded: true,
      typeElaborationRequest: "tgkDocumentElaborationRequest",
    };

    await alignDocumentMetadataAsync(tgkDocumentElaborationRequest);

    LoadingManager.remove(loadingManagerKey);
  };

  const message =
    documentPartToAlign?.unalignedSubParts?.length > 0
      ? formatMessageWithParams("cof.align.modal.message.unalignedsubparts", {
          unalignedSubParts: documentPartToAlign.unalignedSubParts.map((subPart) => subPart.name).join(", "),
        })
      : formatMessage("cof.align.modal.message");

  return (
    <CustomModal
      modalTitle={formatMessage("cof.align.modal.title")}
      messageTitle={formatMessage("cof.align.modal.message.title")}
      message={message}
      type="confirm"
      onHide={() => {}}
      actions={[
        {
          testId: "cancel-btn",
          title: formatMessage("cof.base.cancel"),
          onClick: onCloseModal,
          variant: "white",
        },
        {
          testId: "confirm-btn",
          title: formatMessage("cof.base.confirm"),
          onClick: () => {
            if (documentPartToAlign) {
              void alignDocument();
            } else {
              saveDocument(documentPartToSave);
            }
            onCloseModal();
          },
          variant: "blue",
        },
      ]}
    />
  );
};
